import React from 'react';

const TestResults = ({ chapterScores, totalScore, totalQuestions, onRetakeTest, onBackToMenu }) => {
  const percentage = Math.round((totalScore / totalQuestions) * 100);
  
  // Generate recommendations based on performance
  const generateRecommendations = () => {
    const recommendations = [];
    const poorPerformanceThreshold = 0.67; // Less than 67% (2 out of 3)
    
    Object.entries(chapterScores).forEach(([chapter, score]) => {
      const chapterPercentage = score.correct / score.total;
      if (chapterPercentage < poorPerformanceThreshold) {
        recommendations.push({
          chapter,
          score: `${score.correct}/${score.total}`,
          percentage: Math.round(chapterPercentage * 100),
          priority: chapterPercentage < 0.34 ? 'high' : 'medium' // Less than 33% is high priority
        });
      }
    });

    // Sort by performance (worst first)
    recommendations.sort((a, b) => (a.percentage - b.percentage));
    
    return recommendations;
  };

  const recommendations = generateRecommendations();

  const getPerformanceMessage = () => {
    if (percentage >= 90) {
      return {
        message: "သင်သည် Calculus တွင် အလွန်ကောင်းမွန်သော အခြေခံအဆင့်ရှိပါသည်။",
        color: "text-green-600",
        bgColor: "bg-green-50",
        borderColor: "border-green-200"
      };
    } else if (percentage >= 75) {
      return {
        message: "ကောင်းပါသည်! သင်သည် Calculus အခြေခံများကို ကောင်းစွာ နားလည်ထားပါသည်။",
        color: "text-blue-600",
        bgColor: "bg-blue-50",
        borderColor: "border-blue-200"
      };
    } else if (percentage >= 60) {
      return {
        message: "သင့်တွင် အခြေခံဗဟုသုတ အနည်းငယ် ရှိပါသည်။ နောက်ထပ် လေ့လာမှုများ လုပ်ရန် လိုအပ်ပါသည်။",
        color: "text-yellow-600",
        bgColor: "bg-yellow-50",
        borderColor: "border-yellow-200"
      };
    } else {
      return {
        message: "စိုးရိမ်စရာ မရှိပါ! Calculus သည် ခက်ခဲသော ဘာသာရပ်ဖြစ်ပါသည်။ အခြေခံများကို ပြန်လည် လေ့လာရန် အကြံပြုပါသည်။",
        color: "text-red-600",
        bgColor: "bg-red-50",
        borderColor: "border-red-200"
      };
    }
  };

  const performanceMessage = getPerformanceMessage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Level Test ရလဒ်
            </h1>
            <div className="text-6xl font-bold mb-4">
              <span className={percentage >= 75 ? 'text-green-600' : percentage >= 60 ? 'text-yellow-600' : 'text-red-600'}>
                {totalScore}
              </span>
              <span className="text-gray-400">/{totalQuestions}</span>
            </div>
            <div className="text-xl text-gray-600 mb-4">
              ({percentage}%)
            </div>
          </div>

          {/* Performance Message */}
          <div className={`p-6 rounded-lg border-2 mb-8 ${performanceMessage.bgColor} ${performanceMessage.borderColor}`}>
            <p className={`text-lg font-medium ${performanceMessage.color}`}>
              {performanceMessage.message}
            </p>
          </div>

          {/* Chapter-wise Results */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">အခန်းလိုက် ရမှတ်များ</h2>
            <div className="grid gap-4">
              {Object.entries(chapterScores).map(([chapter, score]) => {
                const chapterPercentage = Math.round((score.correct / score.total) * 100);
                const isGood = chapterPercentage >= 67;
                
                return (
                  <div key={chapter} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-800">{chapter}</h3>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-lg font-bold">
                        {score.correct}/{score.total}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        isGood 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {chapterPercentage}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">လေ့လာရန် အကြံပြုချက်များ</h2>
              <div className="space-y-3">
                {recommendations.map((rec, index) => (
                  <div key={index} className={`p-4 rounded-lg border-l-4 ${
                    rec.priority === 'high' 
                      ? 'bg-red-50 border-red-400' 
                      : 'bg-yellow-50 border-yellow-400'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-800">{rec.chapter}</h3>
                        <p className={`text-sm ${
                          rec.priority === 'high' ? 'text-red-600' : 'text-yellow-600'
                        }`}>
                          {rec.priority === 'high' 
                            ? 'ဦးစားပေး လေ့လာရန် လိုအပ်သည်' 
                            : 'နောက်ထပ် လေ့လာမှု လိုအပ်သည်'
                          }
                        </p>
                      </div>
                      <span className="text-lg font-bold text-gray-600">
                        {rec.score} ({rec.percentage}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium text-blue-800 mb-2">အကြံပြုချက်:</h3>
                <ul className="text-blue-700 text-sm space-y-1">
                  <li>• အပေါ်မှာ ညွှန်ပြထားသော အခန်းများကို ဦးစားပေး လေ့လာပါ</li>
                  <li>• Quiz များကို ပြန်လည် လုပ်ကြည့်ပြီး လေ့ကျင့်ပါ</li>
                  <li>• Integration Methods စာမျက်နှာတွင် နည်းလမ်းများကို ပြန်လည် ကြည့်ရှုပါ</li>
                </ul>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={onRetakeTest}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              Test ကို ပြန်လုပ်မည်
            </button>
            <button
              onClick={onBackToMenu}
              className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
            >
              ပင်မစာမျက်နှာသို့ ပြန်သွားမည်
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestResults;
